import { emailSchema, passwordSchema } from '@ifretes/shared'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { Botao, Campo, Cartao, Carregando, ErroApi, Mensagem } from '../../components/ui'
import { api, ApiError } from '../../lib/api'

/** Confirmacao de e-mail: a pagina que o link do e-mail abre. */
export function ConfirmarEmail() {
  const [params] = useSearchParams()
  const navegar = useNavigate()
  const token = params.get('token') ?? ''
  const [estado, setEstado] = useState<'processando' | 'ok' | 'falha'>('processando')
  const [erro, setErro] = useState<unknown>(null)

  useEffect(() => {
    if (!token) {
      setEstado('falha')
      setErro(new Error('sem token'))
      return
    }

    let cancelado = false
    void (async () => {
      try {
        await api.confirmarEmail(token)
        if (cancelado) return
        setEstado('ok')
        // Manda para o login já sinalizando o sucesso, em vez de deixar a
        // pessoa numa tela terminal sem saber o próximo passo.
        setTimeout(() => navegar('/entrar?confirmado=1', { replace: true }), 1800)
      } catch (err) {
        if (!cancelado) {
          setErro(err)
          setEstado('falha')
        }
      }
    })()

    // O StrictMode do React monta duas vezes em desenvolvimento. Sem esta
    // guarda, a segunda chamada consome um token já consumido e a tela mostraria
    // "link inválido" para uma confirmação que deu certo.
    return () => {
      cancelado = true
    }
  }, [token, navegar])

  if (estado === 'processando') return <Carregando rotulo="Confirmando seu e-mail" />

  return (
    <div className="pagina-centrada">
      <Cartao titulo={estado === 'ok' ? 'E-mail confirmado' : 'Não foi possível confirmar'}>
        {estado === 'ok' ? (
          <Mensagem tipo="success" titulo="Conta ativa">
            Levando você para o login…
          </Mensagem>
        ) : (
          <>
            <ErroApi erro={erro} />
            <p className="ifr-nota ifr-fraco ifr-mt">
              O link pode ter expirado (vale 24 horas) ou já ter sido usado. Tente entrar: se sua
              conta já estiver confirmada, o login funciona normalmente.
            </p>
            <div className="ifr-mt">
              <Link to="/entrar" className="br-button primary block">
                Ir para o login
              </Link>
            </div>
          </>
        )}
      </Cartao>
    </div>
  )
}

/** Pedido de redefinicao de senha. */
export function EsqueciSenha() {
  const [email, setEmail] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [enviado, setEnviado] = useState(false)
  const [erro, setErro] = useState<unknown>(null)
  const [campos, setCampos] = useState<Record<string, string[]>>({})

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    setErro(null)
    setCampos({})

    const r = emailSchema.safeParse(email)
    if (!r.success) {
      setCampos({ email: r.error.issues.map((i) => i.message) })
      return
    }

    setEnviando(true)
    try {
      await api.esqueciSenha(r.data)
      setEnviado(true)
    } catch (err) {
      setErro(err)
    } finally {
      setEnviando(false)
    }
  }

  // A tela de sucesso é a MESMA exista ou não a conta: é o par visual da
  // resposta opaca da API (SPEC 7.3, anti-enumeração). Dizer "e-mail não
  // encontrado" aqui entregaria quem tem conta na plataforma.
  if (enviado) {
    return (
      <div className="pagina-centrada">
        <Cartao titulo="Verifique seu e-mail">
          <Mensagem tipo="info" titulo="Instruções enviadas">
            Se houver uma conta com <strong>{email}</strong>, enviamos para lá o link de
            redefinição. Ele vale por 30 minutos.
          </Mensagem>
          <div className="ifr-mt">
            <Link to="/entrar" className="br-button secondary block">
              Voltar ao login
            </Link>
          </div>
        </Cartao>
      </div>
    )
  }

  return (
    <div className="pagina-centrada">
      <Cartao
        titulo="Esqueci minha senha"
        descricao="Informe seu e-mail e enviaremos um link para criar uma nova senha."
      >
        <ErroApi erro={erro} />
        <form onSubmit={enviar} noValidate className="ifr-pilha">
          <Campo
            id="email"
            rotulo="E-mail"
            tipo="email"
            valor={email}
            aoMudar={setEmail}
            erros={campos.email}
            obrigatorio
            autoComplete="email"
            inputMode="email"
            autoFocus
          />
          <Botao tipo="submit" carregando={enviando} larguraTotal>
            Enviar link
          </Botao>
        </form>
        <p className="ifr-mt-g">
          <Link to="/entrar" className="ifr-marca-cor">
            Voltar ao login
          </Link>
        </p>
      </Cartao>
    </div>
  )
}

/** Definicao da nova senha, a partir do link do e-mail. */
export function RedefinirSenha() {
  const [params] = useSearchParams()
  const navegar = useNavigate()
  const token = params.get('token') ?? ''

  const [senha, setSenha] = useState('')
  const [confirmacao, setConfirmacao] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<unknown>(null)
  const [campos, setCampos] = useState<Record<string, string[]>>({})

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    setErro(null)
    setCampos({})

    const r = passwordSchema.safeParse(senha)
    if (!r.success) {
      setCampos({ password: r.error.issues.map((i) => i.message) })
      return
    }
    if (senha !== confirmacao) {
      setCampos({ confirmacao: ['As senhas não conferem.'] })
      return
    }

    setEnviando(true)
    try {
      await api.redefinirSenha(token, senha)
      navegar('/entrar?senha=ok', { replace: true })
    } catch (err) {
      if (err instanceof ApiError) setCampos(err.camposComErro)
      setErro(err)
      setEnviando(false)
    }
  }

  if (!token) {
    return (
      <div className="pagina-centrada">
        <Cartao titulo="Link inválido">
          <Mensagem tipo="danger" titulo="Sem token">
            Este endereço não tem um token de redefinição. Peça um novo link.
          </Mensagem>
          <div className="ifr-mt">
            <Link to="/esqueci-senha" className="br-button primary block">
              Pedir novo link
            </Link>
          </div>
        </Cartao>
      </div>
    )
  }

  return (
    <div className="pagina-centrada">
      <Cartao
        titulo="Criar nova senha"
        descricao="Ao concluir, todas as suas sessões serão encerradas."
      >
        <ErroApi erro={erro} />
        <form onSubmit={enviar} noValidate className="ifr-pilha">
          <Campo
            id="senha"
            rotulo="Nova senha"
            tipo="password"
            valor={senha}
            aoMudar={setSenha}
            erros={campos.password}
            obrigatorio
            autoComplete="new-password"
            autoFocus
          />
          <Campo
            id="confirmacao"
            rotulo="Repita a nova senha"
            tipo="password"
            valor={confirmacao}
            aoMudar={setConfirmacao}
            erros={campos.confirmacao}
            obrigatorio
            autoComplete="new-password"
          />
          <Botao tipo="submit" carregando={enviando} larguraTotal>
            Salvar nova senha
          </Botao>
        </form>
      </Cartao>
    </div>
  )
}
